// src/api/storeCreditApi.ts
import { http } from "./http";

export interface StoreCreditEntry {
  id: number;
  customer_id: number;
  order_id: number | null;
  amount: number;
  balance_after: number;
  type: "grant" | "refund" | "redemption" | "reversal" | "adjustment" | string;
  reason: string | null;
  created_by: number | null;
  created_at: string;
}

export interface StoreCreditHistory {
  customer_id: number;
  balance: number;
  entries: StoreCreditEntry[];
}

// Current balance for a customer
export const fetchStoreCredit = async (userId: number): Promise<{ balance: number }> => {
  try {
    const response = await http.get(`/store-credit/${userId}`);
    return { balance: Number(response.data?.balance || 0) };
  } catch (error) {
    console.error("Error fetching store credit:", error);
    return { balance: 0 };
  }
};

// Balance + ledger history
export const fetchStoreCreditHistory = async (
  userId: number
): Promise<StoreCreditHistory> => {
  const response = await http.get(`/store-credit/${userId}/history`);
  return {
    customer_id: Number(response.data?.customer_id || userId),
    balance: Number(response.data?.balance || 0),
    entries: Array.isArray(response.data?.entries) ? response.data.entries : [],
  };
};

export interface StoreCreditCustomer {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  balance: number;
  total_granted: number;
  total_redeemed: number;
  tx_count: number;
  last_activity: string | null;
  balance_updated_at: string | null;
}

export interface StoreCreditStats {
  total_outstanding: number;
  customers_with_balance: number;
  customers_total: number;
  total_issued: number;
  total_redeemed: number;
}

export interface StoreCreditOverview {
  stats: StoreCreditStats;
  customers: StoreCreditCustomer[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Dashboard overview: aggregate stats + a paginated, searchable customer list
export const fetchStoreCreditOverview = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
  onlyPositive?: boolean;
}): Promise<StoreCreditOverview> => {
  const response = await http.get("/store-credit/admin/overview", {
    params: {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      search: params.search?.trim() || undefined,
      onlyPositive: params.onlyPositive ? "true" : undefined,
    },
  });
  const d = response.data || {};
  return {
    stats: {
      total_outstanding: Number(d.stats?.total_outstanding || 0),
      customers_with_balance: Number(d.stats?.customers_with_balance || 0),
      customers_total: Number(d.stats?.customers_total || 0),
      total_issued: Number(d.stats?.total_issued || 0),
      total_redeemed: Number(d.stats?.total_redeemed || 0),
    },
    customers: Array.isArray(d.customers) ? d.customers : [],
    page: Number(d.page || 1),
    pageSize: Number(d.pageSize || 20),
    total: Number(d.total || 0),
    totalPages: Number(d.totalPages || 1),
  };
};

// Grant (add) store credit — positive amount
export const grantStoreCredit = async (
  userId: number,
  amount: number,
  reason?: string
) => {
  const response = await http.post("/store-credit/add", {
    customer_id: userId,
    amount,
    reason,
  });
  return response.data;
};

// Signed manual adjustment (+/-)
export const adjustStoreCredit = async (
  userId: number,
  amount: number,
  reason?: string
) => {
  const response = await http.post("/store-credit/adjust", {
    customer_id: userId,
    amount,
    reason,
  });
  return response.data;
};

// Deduct store credit (positive amount → recorded as downward adjustment)
export const useStoreCredit = async (
  userId: number,
  amount: number,
  reason?: string
) => {
  const response = await http.post("/store-credit/use", {
    customer_id: userId,
    amount,
    reason,
  });
  return response.data;
};
